/**
 * Orientation normalisation — get a document upright before detection, OCR, and
 * feature extraction, so a sideways or upside-down capture doesn't flip the
 * aspect ratio / layout heatmap or wreck the OCR.
 *
 * Split into two cheap, dependency-free steps (the 180° step needs OCR, which
 * each environment supplies):
 *
 *  - {@link coarseRotationDegrees} — pure geometry. Text forms horizontal bands
 *    of ink separated by gaps; that makes the row-ink projection vary a lot
 *    (high "line-ness") when text is horizontal, and the column projection vary
 *    when the page is rotated 90°/270°. Comparing the two tells us whether to
 *    rotate 90° to get the text horizontal.
 *  - the remaining 0°-vs-180° flip is resolved by whichever orientation OCRs
 *    better (more readable = upright); the caller does that with its OCR engine.
 */
import type { PixelView } from './document-features';

/** Degrees to rotate the image so its text runs horizontally (0 or 90). */
export function coarseRotationDegrees(img: PixelView): 0 | 90 {
  const { data, width: W, height: H } = img;
  const n = W * H;
  const gray = new Uint8Array(n);
  const hist = new Uint32Array(256);
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    const g = (data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000 | 0;
    gray[i] = g;
    hist[g]++;
  }
  const t = otsu(hist, n);

  // Count ink only inside the paper (between each row's bright edges) so a dark
  // background around a tilted sheet doesn't swamp the text projection.
  const darkRow = new Float64Array(H);
  const darkCol = new Float64Array(W);
  for (let y = 0; y < H; y++) {
    const base = y * W;
    let lo = -1, hi = -1;
    for (let x = 0; x < W; x++) {
      if (gray[base + x] > t) { if (lo < 0) lo = x; hi = x; }
    }
    if (hi - lo < 2) continue;
    for (let x = lo + 1; x < hi; x++) {
      if (gray[base + x] < t) { darkRow[y]++; darkCol[x]++; }
    }
  }
  // Squared coefficient of variation = "how strongly does this axis alternate
  // between text lines and gaps". The axis crossing the text lines wins.
  return cov2(darkCol) > cov2(darkRow) * 1.1 ? 90 : 0;
}

/**
 * Estimate fine skew (the small tilt left after the coarse 90° fix), in degrees,
 * by a projection-profile search: shear the ink pixels by each candidate angle
 * and keep the angle whose horizontal projection is "peakiest" (text lines
 * collapse into sharp bands ⇒ high sum-of-squares). The returned value is the
 * angle to **rotate the image by** to straighten it (i.e. negated skew).
 */
export function estimateSkewDegrees(img: PixelView, maxDeg = 12, step = 0.5): number {
  const { data, width: W, height: H } = img;
  const n = W * H;
  const gray = new Uint8Array(n);
  const hist = new Uint32Array(256);
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    const g = (data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000 | 0;
    gray[i] = g;
    hist[g]++;
  }
  const t = otsu(hist, n);

  // Collect ink pixels, but only those INSIDE the paper (between each row's
  // left/right bright edges) so a dark background around a tilted sheet can't
  // pollute the projection. Sub-sample very dense pages for speed.
  const xs: number[] = [];
  const ys: number[] = [];
  const stride = n > 200_000 ? 2 : 1;
  for (let y = 0; y < H; y += stride) {
    const base = y * W;
    let lo = -1, hi = -1;
    for (let x = 0; x < W; x++) {
      if (gray[base + x] > t) { if (lo < 0) lo = x; hi = x; }
    }
    if (hi - lo < 2) continue;
    for (let x = lo + 1; x < hi; x += stride) {
      if (gray[base + x] < t) { xs.push(x); ys.push(y); }
    }
  }
  if (xs.length < 50) return 0;

  const off = Math.ceil(W * Math.tan((maxDeg * Math.PI) / 180)) + 1;
  const len = H + 2 * off + 2;
  let bestAngle = 0;
  let bestScore = -1;
  for (let a = -maxDeg; a <= maxDeg; a += step) {
    const tan = Math.tan((a * Math.PI) / 180);
    const bins = new Float64Array(len);
    for (let i = 0; i < xs.length; i++) {
      const r = Math.round(ys[i] - xs[i] * tan) + off;
      if (r >= 0 && r < len) bins[r]++;
    }
    let score = 0;
    for (let i = 0; i < len; i++) score += bins[i] * bins[i];
    if (score > bestScore) { bestScore = score; bestAngle = a; }
  }
  // `bestAngle` is the shear that flattens the lines = the skew; rotate by its
  // negative to undo it.
  return -bestAngle;
}

function cov2(a: Float64Array): number {
  let mean = 0;
  for (let i = 0; i < a.length; i++) mean += a[i];
  mean /= a.length;
  if (mean < 1e-6) return 0;
  let v = 0;
  for (let i = 0; i < a.length; i++) v += (a[i] - mean) ** 2;
  v /= a.length;
  return v / (mean * mean);
}

function otsu(hist: Uint32Array, total: number): number {
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, best = 0, bestVar = -1;
  for (let i = 0; i < 256; i++) {
    wB += hist[i];
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += i * hist[i];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > bestVar) { bestVar = between; best = i; }
  }
  return best;
}
